import { useState, useEffect, useRef } from "react";
import { X, User, BookOpen, Barcode, ArrowDownCircle, ArrowLeftRight } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../connector/api";

export default function CheckInModal({ onClose, onSuccess }) {
  const [patronId, setPatronId] = useState("");
  const [bookCode, setBookCode] = useState("");
  const [activeLoans, setActiveLoans] = useState([]);
  const [loading, setLoading] = useState(false);

  const patronRef = useRef(null);
  const bookRef = useRef(null);

  // Auto-focus patron input on initial layout draw
  useEffect(() => {
    patronRef.current?.focus();
  }, []);

  /* ----------------------------------------
     Fetch active loans when patron is scanned
  -----------------------------------------*/
  const fetchPatronLoans = async (code) => {
    if (!code.trim()) return;
    try {
      const res = await api.get("/loans/active_by_patron/", {
        params: { patron_code: code.trim() },
      });
      setActiveLoans(res.data);
    } catch {
      toast.error("Failed to load patron transaction profile");
      setActiveLoans([]);
    }
  };

  /* ----------------------------------------
     Unified Return Ledger Core Check-In Handlers
  -----------------------------------------*/
  const handleCheckInProcess = async (targetBookCode) => {
    const cleanBookCode = (targetBookCode || bookCode).trim();
    if (!patronId.trim() || !cleanBookCode) return;

    try {
      setLoading(true);
      const res = await api.post("/loans/checkin/", {
        patron_id: patronId.trim(),
        book_code: cleanBookCode,
      });

      // Show success toast prioritizing backend message responses
      toast.success(res.data?.detail || `Item "${cleanBookCode}" successfully processing return`);

      // Pop checked-in book out of memory lists instantly 
      setActiveLoans((prev) =>
        prev.filter((l) => (l.barcode || "").trim() !== cleanBookCode)
      );

      setBookCode("");
      onSuccess?.(); // Optional live dashboard refresh trigger
      
      // Auto re-focus book terminal scanner target for high volume lines
      setTimeout(() => bookRef.current?.focus(), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Check-in command execution failed");
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------------------------
     Keyboard & Scanner Routing Anchors
  -----------------------------------------*/
  const handlePatronKey = (e) => {
    if (e.key === "Enter" && patronId.trim()) {
      fetchPatronLoans(patronId);
      setTimeout(() => bookRef.current?.focus(), 50);
    }
  };

  const handleBookKey = (e) => {
    if (e.key === "Enter" && bookCode.trim()) {
      handleCheckInProcess();
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-[#1a1613] rounded-2xl shadow-2xl max-w-2xl w-full border border-[#2e2720] overflow-hidden flex flex-col max-h-[90vh] transform transition-all font-dynamic-title">
        
        {/* Modal Terminal Header */}
        <div className="px-6 py-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400">
              <ArrowDownCircle className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-100">Desk Return Terminal</h3>
              <p className="text-xs text-stone-400 mt-0.5">Scan patron credentials first, then register library book tags to close loans.</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-stone-400 hover:text-stone-200 hover:bg-[#201c17] p-1.5 rounded-xl transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body Container */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1 min-h-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* Step 1: Patron Input field */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wide flex items-center gap-1.5">
                <span>1. Patron Scanner</span>
                {activeLoans.length === 0 && <span className="inline-block h-1.5 w-1.5 bg-amber-400 rounded-full animate-pulse" />}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-2.5 h-4 w-4 text-stone-500" />
                <input
                  ref={patronRef}
                  type="text"
                  placeholder="Scan patron card ID..."
                  value={patronId}
                  onChange={(e) => setPatronId(e.target.value)}
                  onKeyDown={handlePatronKey}
                  className={`w-full bg-[#131110] border text-stone-200 pl-9 pr-3 py-2 rounded-xl text-xs placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition font-mono ${activeLoans.length === 0 ? 'border-amber-500/40 bg-amber-500/5' : 'border-[#2e2720]'}`}
                />
              </div>
            </div>

            {/* Step 2: Book Barcode Input */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wide flex items-center gap-1.5">
                <span>2. Catalog Copy Scanner</span>
                {patronId.trim() && <span className="inline-block h-1.5 w-1.5 bg-emerald-400 rounded-full animate-pulse" />}
              </label>
              <div className="relative">
                <Barcode className="absolute left-3 top-2.5 h-4 w-4 text-stone-500" />
                <input
                  ref={bookRef}
                  type="text"
                  placeholder={patronId.trim() ? "Scan copy tag barcode..." : "Awaiting patron authorization..."}
                  value={bookCode}
                  onChange={(e) => setBookCode(e.target.value)}
                  onKeyDown={handleBookKey}
                  disabled={!patronId.trim()}
                  className={`w-full border text-stone-200 pl-9 pr-3 py-2 rounded-xl text-xs placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition font-mono ${patronId.trim() ? 'bg-[#131110] border-emerald-500/40' : 'bg-[#131110]/50 border-[#2e2720] text-stone-600 cursor-not-allowed'}`}
                />
              </div>
            </div>
          </div>

          {/* Active Outstanding Loans Ledger Layout Component */}
          <div className="space-y-2">
            <div className="flex items-center justify-between border-b border-[#2e2720] pb-2">
              <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-wide">
                Patron Outstanding Balance Ledger
              </h4>
              {patronId.trim() && (
                <span className="text-xs text-stone-400 font-mono bg-[#131110] border border-[#2e2720] px-2 py-0.5 rounded-md">
                  Active Holdings: <strong className="text-stone-200">{activeLoans.length}</strong>
                </span>
              )}
            </div>

            {activeLoans.length === 0 ? (
              <div className="border border-dashed border-[#2e2720] rounded-xl p-8 text-center text-stone-500 flex flex-col items-center justify-center gap-2 bg-[#131110]/30">
                <BookOpen className="h-5 w-5 text-stone-600" />
                <p className="text-xs font-semibold text-stone-300">No active item ledger rows matching workspace profile filters.</p>
                <p className="text-[11px] text-stone-500">Scan a patron ID card to load items assigned out on loan hooks.</p>
              </div>
            ) : (
              <div className="border border-[#2e2720] rounded-xl divide-y divide-[#2e2720] max-h-56 overflow-y-auto bg-[#131110]">
                {activeLoans.map((loan) => (
                  <div
                    key={loan.id}
                    className="p-3 text-xs flex items-center justify-between gap-4 group hover:bg-[#1a1613]/50 transition-colors"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="p-1.5 bg-[#1a1613] text-stone-400 rounded-md shrink-0 border border-[#2e2720]">
                        <BookOpen className="h-4 w-4" />
                      </div>
                      <span className="font-semibold text-stone-200 truncate">{loan.book_title}</span>
                    </div>
                    
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs font-mono text-stone-400 bg-[#1a1613] px-2 py-0.5 border border-[#2e2720] rounded">
                        {loan.barcode}
                      </span>
                      
                      {/* One-click alternative inline bypass check-in button */}
                      <button
                        type="button"
                        onClick={() => handleCheckInProcess(loan.barcode)}
                        disabled={loading}
                        className="text-xs font-bold text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-1 rounded-lg transition"
                      >
                        Check In
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer Controls Area Layout */}
        <div className="px-6 py-4 bg-[#131110] flex items-center justify-end gap-2 border-t border-[#2e2720]">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-[#2e2720] font-mono bg-[#131110] text-stone-300 hover:text-stone-100 hover:bg-[#201c17] font-semibold text-xs rounded-xl transition disabled:opacity-50"
          >
            Close Terminal
          </button>
          
          <button
            type="button"
            disabled={!patronId || !bookCode || loading}
            onClick={() => handleCheckInProcess()}
            className="inline-flex items-center gap-1.5 font-mono px-4 py-2 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-xs rounded-xl shadow transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{loading ? "Processing Return..." : "Check-In"}</span>
            <ArrowLeftRight className="h-4 w-4" />
          </button>
        </div>

      </div>
    </div>
  );
}